import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { VerificationService } from '../../../services/verification/verification.service';
import { NgTemplateOutlet } from '@angular/common';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DialogService } from '../../../services/dialog/dialog.service';
import { DialogData } from '../../../common/components/dialog/dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { PasswordService } from '../../../services/password/password.service';
import { passwordValidator } from '../../../validators/passwordValidator';
import { MatrixService } from '../../../services/matrix/matrix.service';
import { GeneralLoginFlowComponent } from '../../../common/components/general-login-flow/general-login-flow.component';
import { MatIcon } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { KeybackupTestIds } from './keybackup.test-ids';
import { ButtonComponent } from '../../../common/components/button/button.component';
import { PasswordHint } from '../../../common/components/password-hint/password-hint';
import { copyToClipboard } from '../../../services/util';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { MatTooltip } from '@angular/material/tooltip';
import { AlertBoxComponent } from '../../../common/components/alert-box/alert-box.component';

@Component({
  selector: 'app-keybackup',
  imports: [
    MatFormField,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    GeneralLoginFlowComponent,
    MatIcon,
    MatPrefix,
    MatSuffix,
    NgTemplateOutlet,
    ButtonComponent,
    PasswordHint,
    MatTooltip,
    AlertBoxComponent
  ],
  templateUrl: './keybackup.component.html',
  styleUrl: './keybackup.component.scss'
})
/**
 * Die Komponente um sich einen Sicherheitsschlüssel zu erstellen oder um mit Hilfe des Passwortes sich seine Chats
 * wieder zu holen.
 * Begründung: Hier werden keine AFOs erfüllt.
 */
export class KeybackupComponent implements OnInit {
  hasKeybackup = false;
  generatedPassword: string | undefined;
  showSpinner = false;
  isPasswordVisible = false;
  isSuccess = false;
  isVerifying = true;
  redirectUrl = '/';
  isPasswordCopied = false;
  protected readonly testIds = KeybackupTestIds;
  protected readonly clientService = inject(MatrixService);
  protected readonly matDialog = inject(MatDialog);
  protected readonly verificationService = inject(VerificationService);
  private readonly fb = inject(FormBuilder);
  verificationForm = this.fb.group({
    password: ['', [passwordValidator(), Validators.required]],
    repeatedPwd: ['']
  });
  private readonly dialogService = inject(DialogService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly passwordService = inject(PasswordService);
  private readonly destroyRef = inject(DestroyRef);

  get tertiaryBtnText(): string {
    return this.hasKeybackup ? 'Überspringen' : 'Neuen Schlüssel vorschlagen';
  }

  get tertiaryBtnTestIds(): string {
    return this.hasKeybackup ? this.testIds.btnSkipverification : this.testIds.passwordSuggestion;
  }

  tertiaryBtnAction(): void {
    if (this.hasKeybackup) {
      this.skipInitKeyBackup();
    } else {
      this.generateNewPassword();
      this.fillPassword();
    }
  }

  fillPassword(): void {
    if (this.generatedPassword) this.verificationForm.controls['password'].setValue(this.generatedPassword);
  }

  ngOnInit(): void {
    this.generateNewPassword();
    void this.loadKeyBackupStatus();
    this.redirectUrl = this.route.snapshot.queryParamMap.get('redirect') || '/';
  }

  skipInitKeyBackup(): void {
    const dialogData: DialogData = {
      title: 'Chatverläufe bleiben verschlüsselt',
      message: `Wenn Sie diesen Schritt überspringen, bleiben Ihre bisherigen Chatverläufe auf diesem Gerät verschlüsselt und können nicht angezeigt werden. <br><br>
Um vollen Zugriff auf Ihre gesamte Chat‑Historie zu erhalten, empfehlen wir, den Schlüssel zeitnah in den Einstellungen einzugeben.`,
      cancelText: 'Abbrechen',
      confirmText: 'Überspringen',
      dialogContentTestId: 'content_backupkey',
      dialogActionTestId: 'action_backupkey',
      cancelDataTestId: 'cancel_backupkey',
      svgIcon: 'circle-warning',
      confirmDataTestId: KeybackupTestIds.confirmSkipBackupkey
    };
    this.dialogService
      .openWarningDialog(dialogData, '37.5rem', '24.5rem', true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: value => {
          if (value) {
            sessionStorage.setItem('keybackupinteraction', 'interacted');
            this.router.navigateByUrl(this.redirectUrl);
          }
        }
      });
  }

  public generateNewPassword(): void {
    this.generatedPassword = this.passwordService.generatePassword();
  }

  async copyInput(): Promise<void> {
    const passwordwRaw = this.verificationForm.get('password')?.value;
    const password = typeof passwordwRaw === 'string' ? passwordwRaw.trim() : '';

    if (!password) return;
    this.isPasswordCopied = true;
    await copyToClipboard(
      password,
      msg => this.snackbarService.success(msg!),
      msg => this.snackbarService.error(msg!),
      'Passwort kopiert',
      'Passwort konnte nicht kopiert werden'
    );
  }

  async navigateToChat(): Promise<void> {
    if (this.redirectUrl) {
      await this.router.navigateByUrl(this.redirectUrl);
    } else {
      await this.router.navigateByUrl('/rooms');
    }
    this.snackbarService.success('Die Schlüsselverwaltung ist jetzt aktiv');
  }

  protected async setKeyBackup(): Promise<void> {
    this.matDialog.closeAll();
    this.showSpinner = true;
    const reset = false;
    const pwd = this.verificationForm.value.password ?? undefined;

    this.clientService.setVerificationPassword(pwd);

    try {
      // Passwort zuerst deterministisch prüfen (MAC gegen Secret Storage, ohne Server-/Backup-Zugriff).
      // So wird ein echtes Fehlpasswort sofort und endgültig erkannt, während transiente
      // Backup-Konsistenzfehler erst in den nachfolgenden Schritten (mit Retry) behandelt werden.
      if (pwd && !(await this.verificationService.verifyRecoveryKey(pwd))) {
        throw new Error('Der eingegebene Schlüssel ist nicht korrekt.');
      }

      await this.verificationService.initSecretStorage(reset);
      await this.verificationService.initCrossSigning(reset);
      await this.verificationService.initKeyBackup(reset);

      this.isSuccess = true;
    } catch (err) {
      this.isVerifying = false;
      this.isSuccess = false;
      this.showSpinner = false;

      console.error(err);
      throw new Error('Das Passwort war nicht korrekt', { cause: err });
    } finally {
      this.isVerifying = false;
      this.showSpinner = false;
    }
    sessionStorage.setItem('keybackupinteraction', 'interacted');

    await this.verificationService.initHydrateDevice();
    this.clientService.setVerificationPassword(undefined);
  }

  private async loadKeyBackupStatus(): Promise<void> {
    const info = await this.verificationService.hasKeyBackup();
    this.hasKeybackup = !!info;
  }
}
